import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRef } from "react";
import { toast } from "sonner";

import {
  ArticleForm,
  type ArticleFormData,
  type ArticleFormHandle,
} from "~/components/cih/article-form";
import { PageHeader } from "~/components/ui/page-title";
import {
  createArticleFn,
  submitArticleForReviewFn,
} from "~/lib/functions/cih/articles";

export const Route = createFileRoute("/_user/my-coaching/articles/new")({
  staticData: { title: "New article" },
  component: NewArticlePage,
});

function NewArticlePage() {
  const navigate = useNavigate();
  const formRef = useRef<ArticleFormHandle>(null);

  const createMutation = useMutation({
    mutationFn: async ({
      data,
      submitForReview,
    }: {
      data: ArticleFormData;
      submitForReview: boolean;
    }) => {
      const row = await createArticleFn({ data });
      if (submitForReview) {
        await submitArticleForReviewFn({ data: { id: row.id } });
      }
      return row;
    },
    onSuccess: (row, { submitForReview }) => {
      // Clear the dirty baseline before navigating so the unsaved-changes guard
      // doesn't fire on this intentional, post-save navigation.
      formRef.current?.markSaved();
      toast.success(
        submitForReview ? "Article submitted for review" : "Draft saved",
      );
      void navigate({
        to: "/my-coaching/articles/$articleId",
        params: { articleId: row.id },
      });
    },
    onError: (err) => {
      console.error("Failed to save article:", err);
      toast.error(
        err instanceof Error ? err.message : "Failed to save article",
      );
    },
  });

  const handleSave = (data: ArticleFormData) => {
    createMutation.mutate({ data, submitForReview: false });
  };

  const handleSubmitForReview = (data: ArticleFormData) => {
    createMutation.mutate({ data, submitForReview: true });
  };

  return (
    <div className="max-w-4xl space-y-8">
      <PageHeader
        description="Write a coaching article linked to a challenge."
        title="New article"
      />

      <ArticleForm
        ref={formRef}
        isPending={createMutation.isPending}
        onSave={handleSave}
        onSubmitForReview={handleSubmitForReview}
      />
    </div>
  );
}
